import React from 'react';
import { formatCurrencyAmount } from '../../../utils/currency';
import { getStreakWindow } from './streakUtils';
import { formatDisplayDate } from '../../../utils/dateDisplay';

const formatCurrencyNoDecimals = (value, currencyCode = 'USD') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return formatCurrencyAmount(Math.round(n), currencyCode, {
    locale: 'es-ES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    display: 'code',
  });
};

export default function RoasStreakChart({ streak, windowDays }) {
  if (!streak || typeof streak !== 'object') return null;
  const series = Array.isArray(streak.series) ? streak.series : [];
  if (series.length === 0) return null;
  const target = streak.target_roas;
  const targetNum = typeof target === 'number' ? target : Number(target);
  const { rows, windowDays: effectiveWindow } = getStreakWindow({
    series,
    windowDays: windowDays ?? streak.window_days ?? 7,
  });
  const maxRoas = rows.reduce((acc, r) => {
    const n = typeof r?.roas === 'number' ? r.roas : Number(r?.roas);
    return Number.isFinite(n) ? Math.max(acc, n) : acc;
  }, 0);
  const maxForScale = Math.max(0.1, Number.isFinite(targetNum) ? targetNum : 0.1, maxRoas);

  // Date-only labels formatted in UTC (no −1 shift in negative-offset zones).
  const fmtDate = (iso) => formatDisplayDate(iso, { day: '2-digit', month: 'short' }) || String(iso || '');

  return (
    <div className="mt-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs tracking-wide text-white/60 font-semibold">Racha de ROAS</div>
          <div className="mt-0.5 text-[11px] text-white/55">
            Últimos {streak.window_days || series.length} días. Mínimo: {Number.isFinite(targetNum) ? `${targetNum.toFixed(1)}x` : '—'}
          </div>
        </div>
        {streak.days_under_target != null ? (
          <span className="shrink-0 inline-flex items-center rounded-full border border-rose-400/30 bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-100 whitespace-nowrap">
            {streak.days_under_target}/{streak.window_days || series.length} días críticos
          </span>
        ) : null}
      </div>

      <div className="mt-3 relative rounded-lg border border-white/10 bg-white/5 px-3 py-3">
        {Number.isFinite(targetNum) ? (
          <div
            className="absolute left-3 right-3 h-[2px] bg-white/60"
            style={{ top: `${(1 - (targetNum / maxForScale)) * 100}%` }}
            aria-hidden="true"
          />
        ) : null}

        <div
          className="grid gap-2 items-end h-[140px]"
          style={{ gridTemplateColumns: `repeat(${effectiveWindow}, minmax(0, 1fr))` }}
        >
          {rows.map((r, idx) => {
            const roas = r?.roas;
            const roasNum = typeof roas === 'number' ? roas : Number(roas);
            const spend = r?.spend;
            const conv = r?.conversions;
            const hasRoas = Number.isFinite(roasNum) && roasNum > 0;
            const isCritical = Number.isFinite(targetNum) && (Number.isFinite(roasNum) ? roasNum < targetNum : true);
            const noConv = Number.isFinite(conv) && Number(conv) === 0;
            const barH = hasRoas ? Math.max(6, Math.round((roasNum / maxForScale) * 130)) : 6;
            const barTone = noConv
              ? 'bg-amber-500/35'
              : (isCritical ? 'bg-rose-500/45' : 'bg-emerald-500/35');
            const label = fmtDate(r?.date);
            const barValueLabel = hasRoas ? `${roasNum.toFixed(1)}x` : '—';
            const tooltip = `${label} · ROAS ${barValueLabel} · Gasto ${spend != null ? formatCurrencyNoDecimals(spend, 'UYU') : '—'} · Conv. ${conv != null ? conv : '—'}`;
            return (
              <div key={`${r?.date || idx}-${idx}`} className="min-w-0 flex flex-col items-center justify-end gap-2">
                <div className="text-[10px] font-semibold tabular-nums text-white/70 whitespace-nowrap" title={barValueLabel}>
                  {barValueLabel}
                </div>
                <div
                  className={`w-full rounded-md ${barTone} border border-white/10`}
                  style={{ height: `${barH}px` }}
                  title={tooltip}
                  aria-label={tooltip}
                />
                <div className="text-[10px] text-white/60 font-semibold tabular-nums whitespace-nowrap">{label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
